import { useEffect, useState } from 'react'
import { useRoomViewStore } from 'store/roomView/roomView.store'
import { useUpdatePatientStatusMutation } from '../hooks/surgeries.hook'

import { enqueueSnackbar } from 'notistack'
import { TPatientCardProps } from './PatientCard.types'
import { EPatientStatus, TRoomViewRoom } from '../RoomView.types'
import { useNavigate } from 'react-router-dom'
import { getDurationBetweenDates } from '../helpers'
import { shouldAllowPatientStatusRestrictedActions } from 'lib/utils/patientStatusUtils'

export const usePatientCardLogic = (props: TPatientCardProps) => {
  const { surgery } = props
  const navigate = useNavigate()
  const { rooms, scheduledSurgeries, refetchAll, roomViewSettings } =
    useRoomViewStore()

  const [updatePatientStatus] = useUpdatePatientStatusMutation()

  const [waitingTime, setWaitingTime] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [roomMenuAnchorEl, setRoomMenuAnchorEl] = useState<null | HTMLElement>(
    null
  )

  const { allowPatientStatusRestrictedActions } = useRoomViewStore()

  const disableDocumentSurgery = !shouldAllowPatientStatusRestrictedActions(
    allowPatientStatusRestrictedActions,
    surgery.patientStatusHistory[0]?.status
  )

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setRoomMenuAnchorEl(null)
  }

  const handleRoomMenuClose = () => {
    setRoomMenuAnchorEl(null)
  }

  const handleMoveToRoom = async (room: TRoomViewRoom) => {
    try {
      await updatePatientStatus({
        variables: {
          procedureId: surgery._id,
          patientStatus: EPatientStatus.ASSIGN_ROOM,
          roomId: room.id,
        },
      })
      refetchAll()
      enqueueSnackbar(`Patient moved to room ${room.name}`, {
        variant: 'success',
      })
    } catch (error: unknown) {
      enqueueSnackbar((error as Error).message, {
        variant: 'error',
      })
    }
    handleRoomMenuClose()
    handleClose()
  }

  const handleNavigateToSurgery = () => {
    navigate(`/surgeries/${surgery._id}`)
    handleClose()
  }

  const handleBackClick = async () => {
    try {
      await updatePatientStatus({
        variables: {
          procedureId: surgery._id,
          patientStatus: EPatientStatus.SCHEDULED,
        },
      })
      refetchAll()
      enqueueSnackbar(`Patient moved back to scheduled`, {
        variant: 'success',
      })
    } catch (error: unknown) {
      enqueueSnackbar((error as Error).message, {
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    const calculateWaitingTime = () => {
      const createdAt = surgery.patientStatusHistory[0]?.info?.createdAt
      if (createdAt) {
        const currentTime = new Date()
        const duration = getDurationBetweenDates(createdAt, currentTime)
        setWaitingTime(duration)
      }
    }

    calculateWaitingTime()

    const interval = setInterval(calculateWaitingTime, 60000)

    return () => clearInterval(interval)
  }, [surgery.patientStatusHistory])

  return {
    rooms,
    anchorEl,
    isModalOpen,
    waitingTime,
    roomViewSettings,
    roomMenuAnchorEl,
    scheduledSurgeries,
    disableDocumentSurgery,
    setAnchorEl,
    handleClick,
    handleClose,
    setIsModalOpen,
    handleMoveToRoom,
    setRoomMenuAnchorEl,
    handleRoomMenuClose,
    handleNavigateToSurgery,
    handleBackClick,
  }
}
